import { useQuery } from '@tanstack/react-query';
import { bridgeApi } from '@/lib/bridgeApi';

const AUTH_STORAGE_KEY = 'patakare_auth_management';

export type ModuleVisibilityStatus = 'LIVE' | 'HIDDEN' | 'COMING_SOON' | 'BETA';

export interface ModuleConfig {
  login_enabled: boolean;
  signup_enabled: boolean;
  visibility_status?: ModuleVisibilityStatus;
}

export type AuthConfig = Record<string, ModuleConfig>;

/**
 * Hook to check if login/signup is enabled for a specific module.
 * Used by Auth pages to enforce Admin Auth Management toggles.
 */
export function useAuthConfig(moduleKey: string) {
  const { data: config } = useQuery({
    queryKey: ['auth-config-check'],
    queryFn: async (): Promise<AuthConfig | null> => {
      try {
        const data = await bridgeApi.call<any>('get_auth_config', {});
        if (data && typeof data === 'object' && !Array.isArray(data)) {
          // Cache locally
          localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(data));
          return data as AuthConfig;
        }
      } catch {
        // Fallback to localStorage
        try {
          const stored = localStorage.getItem(AUTH_STORAGE_KEY);
          if (stored) return JSON.parse(stored) as AuthConfig;
        } catch { /* ignore */ }
      }
      // Final fallback to localStorage
      try {
        const stored = localStorage.getItem(AUTH_STORAGE_KEY);
        if (stored) return JSON.parse(stored) as AuthConfig;
      } catch { /* ignore */ }
      return null;
    },
    staleTime: 5 * 60 * 1000, // 5 min cache
  });

  const moduleConfig = config?.[moduleKey];

  return {
    loginEnabled: moduleConfig?.login_enabled ?? true, // default enabled
    signupEnabled: moduleConfig?.signup_enabled ?? true,
    visibilityStatus: (moduleConfig?.visibility_status ?? 'LIVE') as ModuleVisibilityStatus,
  };
}

/**
 * Returns the entire auth/visibility config map.
 * Use this for navbar/category-grid filtering.
 */
export function useAllModulesConfig() {
  const { data: config } = useQuery({
    queryKey: ['auth-config-check'],
    queryFn: async (): Promise<AuthConfig | null> => {
      // 1) Try Bridge API (source of truth across all devices/browsers)
      try {
        const data = await bridgeApi.call<any>('get_auth_config', {});
        if (data && typeof data === 'object' && !Array.isArray(data)) {
          try { localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(data)); } catch { /* ignore */ }
          return data as AuthConfig;
        }
      } catch { /* fall through to localStorage */ }
      // 2) Fallback to localStorage (offline / API down)
      try {
        const stored = localStorage.getItem(AUTH_STORAGE_KEY);
        if (stored) return JSON.parse(stored) as AuthConfig;
      } catch { /* ignore */ }
      return null;
    },
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
  return config || {};
}
